<template>
  <div class="error-page">
    <div class="mpf-u-margin-bottom-6">
      <p v-if="showNhsError() || showCode()">
        Error: <code class="mpf-u-display-inline">{{ getFirstCode() }}</code>
      </p>
      <p v-if="getMainMessage() && !getFirstMessage()" class="mpf-preserve-newlines">
        {{ getMainMessage() }}
      </p>

      <p
        v-if="getFirstMessage()"
        v-for="(line, i) in getFirstMessage().split('\n')"
        :key="i"
        class="mpf-error-message-to-p-tag"
      >
        {{ line }}
      </p>

      <p v-if="!getMainMessage() && !getFirstMessage()">Something went wrong. Please try again.</p>
      <template v-if="errorStore.errors.length">
        <template v-for="c in errorStore.errors[0].components" :key="c.name">
          <!-- NB. Only tenant info component setup -->
          <component
            :is="resolveErrorComponent(c.name as ErrorComponentName)"
            v-bind="c.props"
            :actionOpeningTimes="
              tenantStore.actionOpeningAvailability(getFirstRequestType())?.openingTimes
            "
            :actionAlwaysOpen="
              tenantStore.actionOpeningAvailability(getFirstRequestType())?.alwaysOpen
            "
            :practicePhoneNumber="tenantStore.tenant?.localOrganisation.phone"
          />
        </template>
      </template>
    </div>
    <div>
      <MpfButton v-if="showStartAgain" label="Start again" @click="startAgain" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import * as Sentry from '@sentry/vue';
  import { useErrorStore } from '@/stores/useErrorStore';
  import { useTenantStore } from '@/stores/useTenantStore';
  import { useRouteMetaOverrideStore } from '@/stores/useRouteMetaOverrideStore';
  import { ErrorComponentName, errorComponentRegistry } from '@/data/errors/errorComponentRegistry';
  import { computed, defineAsyncComponent, onMounted, onUnmounted } from 'vue';
  import type { Component } from 'vue';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import { router } from '@/router';
  import { getTenantFromPath } from '../composables/useTenantNavigation';
  import { useRoute } from 'vue-router';
  import { storeToRefs } from 'pinia';
  const tenantStore = useTenantStore();
  const errorStore = useErrorStore();
  const metaOverride = useRouteMetaOverrideStore();
  import { useNhsAppStore } from '@/stores/useNhsAppStore';
  const nhsAppStore = useNhsAppStore();
  const { isNhsWebApp } = storeToRefs(nhsAppStore);
  const {
    getAttemptedUrl,
    getErrorHttpReferrer,
    getFirstTitle,
    getFirstMessage,
    getFirstCode,
    getMainMessage,
    getFirstRequestType,
    showNhsError,
    showCode,
  } = errorStore;

  const route = useRoute();
  const cache = new Map<ErrorComponentName, Component>();

  function resolveErrorComponent(name: ErrorComponentName): Component {
    const cached = cache.get(name);
    if (cached) return cached;
    const asyncComp = defineAsyncComponent(errorComponentRegistry[name]);
    cache.set(name, asyncComp);
    return asyncComp;
  }

  const startAgain = () => router.push(`/start`);
  const showStartAgain = computed(() => {
    return tenantStore?.tenant?.tenantId && isNhsWebApp.value === false;
  });

  onMounted(async () => {
    const tenantIdFromUrl = getTenantFromPath(route.path);
    if (tenantIdFromUrl) {
      await tenantStore.fetchTenant(tenantIdFromUrl);
    }
    if (getFirstCode()) {
      // NB. logging known error codes as info
      Sentry.captureMessage(`${getFirstCode()}`, {
        level: 'info',
      });
    } else {
      Sentry.captureMessage('None coded error', {
        level: 'error',
        tags: {
          error_type: 'initial_url',
          error_http_referrer: getErrorHttpReferrer() || 'none',
        },
        contexts: {
          navigation: {
            attempted: getAttemptedUrl(),
            current: window.location.pathname + window.location.search + window.location.hash,
          },
        },
      });
    }
  });

  if (errorStore.errors.length) {
    metaOverride.setOverrides({ layoutPageTitle: getFirstTitle(), backButton: true });
  }
  onUnmounted(() => {
    metaOverride.clearOverrides();
  });
</script>
